import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ViewProps } from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * An optional `Modal` provider for tracking things like active (aka open)
 * modals, etc.
 *
 * Usage:
 *
 * 1. Put the provider at the root of your application.
 * 2. Use the `useModalContext` hook to access the context.
 */
export const ModalProvider = (props: ModalProviderProps) => {
  const { children } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [activeModal, setActiveModal] = useState<ActiveModal | undefined>(
    undefined,
  );

  const hasActiveModal = activeModal !== undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { current: resetActiveModal } = useRef(() => {
    setActiveModal(undefined);
  });

  // ─────────────────────────────────────────────────────────────────────

  const contextValue = useMemo(
    () => ({ activeModal, hasActiveModal, setActiveModal, resetActiveModal }),
    [activeModal, hasActiveModal, resetActiveModal],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => useContext(ModalContext);

// ─── Context ─────────────────────────────────────────────────────────────────

export const ModalContext = createContext<ModalContextValue>(undefined);

// ─── Types ───────────────────────────────────────────────────────────────────

type ModalProviderProps = Pick<ViewProps, 'children'>;

type ActiveModal = {
  /**
   * Modals can have an optional `id`, which can be used to identify
   * them amongst other modals and perform conditional actions depending on that.
   */
  readonly id?: string;
};

type ModalContextValue =
  | {
      readonly activeModal: ActiveModal | undefined;
      readonly hasActiveModal: boolean;
      readonly setActiveModal: (activeModal: ActiveModal) => void;
      readonly resetActiveModal: () => void;
    }
  | undefined;
